// Hierarchical highlight-target registry for the in-app Help/Docs system.
//
// Every user-visible control we can point at carries a `data-help-target="<key>"`
// attribute whose value is a dot-separated path, e.g. `mainapp.addjob.fromurl`.
// The path encodes the logical hierarchy; `revealedBy` declares which ancestor
// element to click to make a nested control appear (menu → modal → accordion).
//
// Conventions:
//  - `mainapp.*`            controls on the main dashboard app
//  - `answers.*`            controls on the Application Q&A page
//  - `...resume0`, `job0`   the FIRST item in a list (we only ever point at one)
//  - `...resumeN`           the resume currently OPEN in the Edit Resume lightbox
//  - `...jobN.*`            fields/buttons of the current job (first match wins)
//
// First-match: multiple cards/rows share the same key; querySelector returns the
// first, which is exactly the "we only need one" behaviour we want. Phase-specific
// buttons only render on cards of that phase, so the first match is in that phase.
//
// Resolution is registry-driven (not DOM-nesting) because modals/dropdowns are
// portaled out of their logical parent. `isTargetActive`/`inactiveHint` are
// non-mutating; `highlightTarget` may click ancestors to reveal a hidden target.

export interface HelpTarget {
  key: string; // full dot-path; also the data-help-target attribute value
  label: string; // human description shown in the admin target picker
  revealedBy?: string; // key of an ancestor element to click to reveal this one
  revealDelayMs?: number; // wait after clicking the opener (modals need longer)
  inactiveHint?: string; // tooltip when inactive and not auto-revealable
  filteredHint?: string; // tooltip for job targets when jobs exist but are filtered out
}

const RESUME_SECTIONS = [
  'contact', 'summary', 'skills', 'experience', 'education',
  'awards', 'presentations', 'publications', 'portfolios',
] as const;

const SECTION_LABELS: Record<string, string> = {
  contact: 'Contact', summary: 'Summary', skills: 'Skills', experience: 'Experience',
  education: 'Education', awards: 'Awards', presentations: 'Presentations',
  publications: 'Publications', portfolios: 'Portfolios',
  highlights: 'Highlights', coverletter: 'Cover Letter',
};

// Highlights has no editable content, but it does carry a heading (rename +
// reorder + generation type) in the Edit Resume lightbox. Cover Letter is
// pinned last there with only a generation-type chip.
const EDITOR_SECTIONS = [...RESUME_SECTIONS, 'highlights', 'coverletter'];
// Sections whose heading can be renamed / reordered in the editor. Contact is
// pinned first and coverletter last, and coverletter isn't a resume section.
const EDITOR_RENAMEABLE = new Set<string>([...RESUME_SECTIONS, 'highlights']);
const EDITOR_REORDERABLE = new Set<string>(
  [...RESUME_SECTIONS, 'highlights'].filter(s => s !== 'contact'));

// Job phases and the action buttons that render in each. Keys mirror the app's
// phase names lowercased (Follow-Up → followup).
const JOB_PHASE_ACTIONS: Record<string, string[]> = {
  queued: ['generate', 'skip', 'moveto', 'phasedisplay'],
  generating: ['generate', 'skip', 'moveto', 'phasedisplay'],
  ready: ['apply', 'applied', 'skip', 'moveto', 'phasedisplay'],
  applied: ['followup', 'declined', 'moveto', 'phasedisplay'],
  followup: ['interview', 'skip', 'moveto', 'phasedisplay'],
  interviewing: ['practice', 'skip', 'moveto', 'phasedisplay'],
  accepted: ['congrats', 'skip', 'moveto', 'phasedisplay'],
  declined: ['delete', 'skip', 'moveto', 'phasedisplay'],
  skipped: ['delete', 'skip', 'moveto', 'phasedisplay'],
  errored: ['delete', 'skip', 'moveto', 'phasedisplay'],
  expired: ['delete', 'skip', 'moveto', 'phasedisplay'],
  trash: ['delete', 'skip', 'moveto', 'phasedisplay'],
};

const ACTION_LABELS: Record<string, string> = {
  generate: 'Generate Resume', skip: 'Skip', moveto: 'Move To…', phasedisplay: 'Phase badge',
  apply: 'Apply', applied: 'Applied', followup: 'Mark for Follow-Up', declined: 'Declined',
  interview: 'Mark for Interview', practice: 'Practice Interview Questions', congrats: 'Celebrate',
  delete: 'Mark for Deletion',
};

const ADD_A_JOB = 'Add a job to see this in action';
const SELECT_A_PHASE = 'Select a phase that contains jobs to see this in action';
const OPEN_RESUME_EDITOR = 'Open a resume in the editor to use this';
const OPEN_JOB_EDIT = 'Open a job’s edit panel to use this';

function t(key: string, label: string, extra: Partial<HelpTarget> = {}): HelpTarget {
  return { key, label, ...extra };
}

function buildCatalog(): HelpTarget[] {
  const out: HelpTarget[] = [];

  // --- top-level nav / chrome ---
  out.push(
    t('mainapp.upgradenow', 'Upgrade Now button'),
    t('mainapp.freegenerations', 'Free Generations counter'),
    t('mainapp.viewmain', 'Grid view switch'),
    t('mainapp.viewkanban', 'Kanban view switch'),
    t('mainapp.viewhelp', 'Help (?) toggle'),
    t('mainapp.terms', 'Terms link'),
    t('mainapp.privacy', 'Privacy link'),
    t('mainapp.features', 'Feature Selector'),
    t('mainapp.features.resumeeditor', 'Resume Editor (features)', { revealedBy: 'mainapp.features', revealDelayMs: 200 }),
    t('mainapp.features.jobmanager', 'Job Manager (features)', { revealedBy: 'mainapp.features', revealDelayMs: 200 }),
    t('mainapp.features.writingsample', 'Writing Sample (features)', { revealedBy: 'mainapp.features', revealDelayMs: 200 }),
    t('mainapp.features.applicationqa', 'Application Q&A (features)', { revealedBy: 'mainapp.features', revealDelayMs: 200 }),
    t('mainapp.features.interviewqa', 'Interview Q&A (features)', { revealedBy: 'mainapp.features', revealDelayMs: 200 }),
    t('mainapp.features.activityreport', 'Activity Dashboard (features)', { revealedBy: 'mainapp.features', revealDelayMs: 200 }),
    t('mainapp.features.help', 'Help & Tips (features)', { revealedBy: 'mainapp.features', revealDelayMs: 200 }),
  );

  // --- user menu ---
  out.push(
    t('mainapp.user', 'User menu button'),
    t('mainapp.user.settings', 'Settings (user menu)', { revealedBy: 'mainapp.user', revealDelayMs: 200 }),
    t('mainapp.user.website', 'Website (user menu)', { revealedBy: 'mainapp.user', revealDelayMs: 200 }),
    t('mainapp.user.feedback', 'Send Feedback (user menu)', { revealedBy: 'mainapp.user', revealDelayMs: 200 }),
    t('mainapp.user.version', 'Version (user menu)', { revealedBy: 'mainapp.user', revealDelayMs: 200 }),
    t('mainapp.user.logout', 'Logout (user menu)', { revealedBy: 'mainapp.user', revealDelayMs: 200 }),
  );

  // --- add job ---
  out.push(t('mainapp.addjob', 'Add Job button'));
  out.push(
    t('mainapp.addjob.fromurl', 'Add Job → From URL', { revealedBy: 'mainapp.addjob' }),
    t('mainapp.addjob.manualentry', 'Add Job → Manual Entry', { revealedBy: 'mainapp.addjob' }),
    t('mainapp.addjob.fromemail', 'Add Job → From Email', { revealedBy: 'mainapp.addjob' }),
  );
  out.push(
    t('mainapp.addjob.fromurl.url', 'From URL: URL field', { revealedBy: 'mainapp.addjob.fromurl', revealDelayMs: 320 }),
    t('mainapp.addjob.fromurl.importjob', 'From URL: Import Job button', { revealedBy: 'mainapp.addjob.fromurl', revealDelayMs: 320 }),
  );
  for (const [seg, lbl] of [
    ['company', 'Company'], ['jobtitle', 'Job Title'], ['jobdescription', 'Job Description'],
    ['location', 'Location'], ['salary', 'Salary'], ['joburl', 'Job URL'],
  ] as const) {
    out.push(t(`mainapp.addjob.manualentry.${seg}`, `Manual Entry: ${lbl} field`,
      { revealedBy: 'mainapp.addjob.manualentry', revealDelayMs: 320 }));
  }
  out.push(
    t('mainapp.addjob.fromemail.email', 'From Email: forwarding address', { revealedBy: 'mainapp.addjob.fromemail', revealDelayMs: 320 }),
    t('mainapp.addjob.fromemail.gotit', 'From Email: Got it button', { revealedBy: 'mainapp.addjob.fromemail', revealDelayMs: 320 }),
  );

  // --- phase filters (sidebar) ---
  const phaseLabels: [string, string][] = [
    ['queued', 'Queued'], ['generating', 'Generating'], ['ready', 'Ready'], ['applied', 'Applied'],
    ['followup', 'Follow-Up'], ['interviewing', 'Interviewing'], ['declined', 'Declined'],
    ['accepted', 'Accepted'], ['skipped', 'Skipped'], ['errored', 'Errored'],
    ['expired', 'Expired'], ['trash', 'Trash'],
  ];
  for (const [seg, lbl] of phaseLabels) out.push(t(`mainapp.phases.${seg}`, `Phase filter: ${lbl}`));
  out.push(
    t('mainapp.phases.active', 'Phase filter: All Active'),
    t('mainapp.phases.all', 'Phase filter: All Jobs'),
  );

  // --- resumes: selector + editor ---
  out.push(
    t('mainapp.resumes', 'Resume selector'),
    t('mainapp.resumes.addresume', 'Add Resume', { revealedBy: 'mainapp.resumes', revealDelayMs: 240 }),
    t('mainapp.resumes.resume0', 'First resume in the list', { revealedBy: 'mainapp.resumes', revealDelayMs: 240 }),
    t('mainapp.resumes.resume0.edit', 'First resume: edit pencil', { revealedBy: 'mainapp.resumes', revealDelayMs: 240 }),
    // Editor lightbox is a precondition (not auto-opened): grey until a resume is open.
    t('mainapp.resumes.resumeN', 'Open resume editor', { inactiveHint: OPEN_RESUME_EDITOR }),
  );
  // Everything below hangs off the open editor lightbox.
  const R = 'mainapp.resumes.resumeN';

  // Top bar (left→right), then the always-visible cards, then the footer bar.
  const editorControls: [string, string][] = [
    ['resumename', 'resume name in the top bar — click the pencil to rename inline'],
    ['import', 'Import ▾ menu in the top bar (PDF / data file)'],
    ['export', 'Export ▾ menu in the top bar (PDF / Text / Data File)'],
    ['aireview', 'Review resume button in the top bar — runs the AI review'],
    ['scorecard', 'Scorecard chip in the top bar — opens the full scorecard'],
    ['preview', 'Preview toggle in the top bar — slides the live preview in/out'],
    ['resumemenu', 'Resume ▾ actions menu in the top bar'],
    ['close', 'close (✕) button in the top bar'],
    ['basics', 'Name card — the candidate’s professional name (first card)'],
    ['resumeurls', 'Resume URLs card — the generic URL + role-targeted resumes'],
    ['masterurl', 'resume URL path field (Resume URLs card)'],
    ['profname', 'candidate’s full professional name (Name card)'],
    ['synonyms', 'Define Synonyms card — interchangeable wording sets'],
    ['synonyms.suggested', 'Define Synonyms → Suggested pairs from your generated jobs (Add / Decline)'],
    ['impliedfacts', 'Implied Facts card — facts your resume implies, for you to confirm'],
    ['impliedfacts.confirmall', 'Implied Facts → Confirm all button'],
    ['demographics', 'Demographics card — optional, never printed on the resume'],
    ['savestate', 'saved / unsaved-changes indicator in the footer'],
    ['sectionposition', '“Section N of M” readout in the footer'],
    ['prevsection', 'Previous section stepper in the footer'],
    ['nextsection', 'Next section stepper in the footer'],
    ['cancel', 'Cancel button in the footer — closes without saving'],
    ['save', 'Save button in the footer — saves and keeps the editor open'],
    ['saveclose', 'Save and Close button in the footer'],
  ];
  for (const [seg, lbl] of editorControls) {
    out.push(t(`${R}.${seg}`, `Editor: ${lbl}`, { revealedBy: R }));
  }

  // Import ▾ / Export ▾ menu items (revealed by their own menu button).
  for (const [seg, lbl, opener] of [
    ['loadfrompdf', 'Import → Load from PDF (parses an existing resume PDF)', 'import'],
    ['import.fromfile', 'Import → Load from file (YAML or JSON resume data)', 'import'],
    ['export.pdf', 'Export → PDF (full resume, nothing trimmed)', 'export'],
    ['export.text', 'Export → Text File (plain text for ATS paste-in)', 'export'],
    ['export.datafile', 'Export → Data File (YAML backup of this resume)', 'export'],
  ] as [string, string, string][]) {
    out.push(t(`${R}.${seg}`, `Editor: ${lbl}`, { revealedBy: `${R}.${opener}`, revealDelayMs: 200 }));
  }

  // Resume ▾ menu: THIS RESUME actions, then SWITCH RESUMES, then New Resume.
  for (const [seg, lbl] of [
    ['resumemenu.viewhtml', 'Resume ▾ → View HTML (opens the published resume page)'],
    ['resumemenu.viewpdf', 'Resume ▾ → View PDF'],
    ['resumemenu.clone', 'Resume ▾ → Clone Resume'],
    ['resumemenu.delete', 'Resume ▾ → Delete Resume'],
    ['resumemenu.switchresumes', 'Resume ▾ → Switch resumes list'],
    ['resumemenu.switchresumes.current', 'Resume ▾ → the resume being edited (“Currently editing”)'],
    ['resumemenu.switchresumes.resume0', 'Resume ▾ → first other resume you can switch to'],
    ['resumemenu.newresume', 'Resume ▾ → New Resume'],
  ] as [string, string][]) {
    out.push(t(`${R}.${seg}`, `Editor: ${lbl}`, { revealedBy: `${R}.resumemenu`, revealDelayMs: 240 }));
  }

  // Live preview slide-out (the toggle itself is a top-bar control above).
  for (const [seg, lbl] of [
    ['preview.panel', 'live preview panel — typeset mini-resume of the current draft'],
    ['preview.source', 'live preview: what to show (this draft, or a role-targeted resume)'],
    ['preview.pagefit', 'page-fit meter at the bottom of the live preview'],
    ['preview.close', 'close button on the live preview panel'],
  ] as [string, string][]) {
    out.push(t(`${R}.${seg}`, `Editor: ${lbl}`, { revealedBy: `${R}.preview`, revealDelayMs: 240 }));
  }

  // Left section-nav rail. Rows/bands render in BOTH the expanded panel and the
  // collapsed icon rail; the detail bits (chips, pointers, grips, readouts) only
  // exist expanded, so they are revealed by the collapse toggle.
  const NAV = `${R}.nav`;
  out.push(
    t(NAV, 'Editor: left section-nav rail', { revealedBy: R }),
    t(`${NAV}.header`, 'Editor nav: score header block (top of the rail)', { revealedBy: R }),
    t(`${NAV}.score`, 'Editor nav: resume score number — opens the scorecard', { revealedBy: R }),
    t(`${NAV}.collapse`, 'Editor nav: collapse / expand the rail', { revealedBy: R }),
    t(`${NAV}.scorebar`, 'Editor nav: resume score progress bar', { revealedBy: `${NAV}.collapse` }),
    t(`${NAV}.recommendations`, 'Editor nav: AI-review recommendations readout (bottom of the rail)', { revealedBy: `${NAV}.collapse` }),
    t(`${NAV}.note`, 'Editor nav: “nothing here is the final resume” note', { revealedBy: `${NAV}.collapse` }),
  );
  for (const [band, lbl] of [
    ['identity', 'Identity'], ['story', 'Story'], ['evidence', 'Evidence'],
    ['finish', 'Finish'], ['voice', 'Voice'], ['chromeext', 'Chrome Ext'],
  ] as [string, string][]) {
    out.push(t(`${NAV}.band.${band}`, `Editor nav: ${lbl} band (drop sections here to move them)`, { revealedBy: R }));
  }
  // One row per editor block: the six pinned rows plus every resume section.
  const NAV_ROWS: [string, string][] = [
    ['basics', 'Name'], ['contact', 'Contact Info'], ['demographics', 'Demographics'],
    ['resumeurls', 'Resume URLs'], ['synonyms', 'Define Synonyms'],
    ['impliedfacts', 'Implied Facts'], ['writingsample', 'Writing Sample'],
    ...RESUME_SECTIONS.filter(s => s !== 'contact').map(s => [s, SECTION_LABELS[s]] as [string, string]),
    ['highlights', 'Highlights'], ['coverletter', 'Cover letter'],
  ];
  const NAV_PINNED = new Set(['basics', 'contact', 'demographics', 'resumeurls', 'synonyms', 'impliedfacts', 'writingsample', 'coverletter']);
  // Rows with no generation type and no print position (they never render on a
  // resume) get only the row target itself.
  const NAV_NO_GENTYPE = new Set(['basics', 'resumeurls', 'synonyms', 'impliedfacts', 'writingsample', 'demographics']);
  for (const [row, lbl] of NAV_ROWS) {
    out.push(t(`${NAV}.row.${row}`, `Editor nav: ${lbl} row`, { revealedBy: R }));
    if (!NAV_NO_GENTYPE.has(row)) {
      out.push(t(`${NAV}.row.${row}.gentype`, `Editor nav: ${lbl} generation-type chip (click to cycle)`, { revealedBy: `${NAV}.collapse` }));
      out.push(t(`${NAV}.row.${row}.position`, `Editor nav: ${lbl} print-position pointer (N of M)`, { revealedBy: `${NAV}.collapse` }));
    }
    if (!NAV_PINNED.has(row)) {
      out.push(t(`${NAV}.row.${row}.drag`, `Editor nav: ${lbl} drag handle (reorder / move between bands)`, { revealedBy: `${NAV}.collapse` }));
    }
  }
  out.push(t(`${NAV}.row.demographics.chip`, 'Editor nav: Demographics PRIVATE badge', { revealedBy: `${NAV}.collapse` }));

  // Section cards in the scroll pane.
  for (const sec of EDITOR_SECTIONS) {
    out.push(t(`${R}.${sec}`, `Editor section: ${SECTION_LABELS[sec]}`, { revealedBy: R }));
    out.push(t(`${R}.${sec}.gentype`, `Editor: ${SECTION_LABELS[sec]} generation type`, { revealedBy: `${R}.${sec}` }));
    if (EDITOR_RENAMEABLE.has(sec)) {
      out.push(t(`${R}.${sec}.title`, `Editor: ${SECTION_LABELS[sec]} section name (click to rename)`, { revealedBy: `${R}.${sec}` }));
    }
    if (EDITOR_REORDERABLE.has(sec)) {
      out.push(t(`${R}.${sec}.reorder`, `Editor: ${SECTION_LABELS[sec]} reorder arrows`, { revealedBy: `${R}.${sec}` }));
    }
  }

  // --- job cards ---
  out.push(t('mainapp.job0', 'A job card', { inactiveHint: ADD_A_JOB, filteredHint: SELECT_A_PHASE }));
  const jobMeta: [string, string][] = [
    ['company', 'Company name'], ['title', 'Role title'], ['location', 'Location'],
    ['salary', 'Salary'], ['age', 'Posting age'],
    ['source', 'Source'], ['edit', 'Edit job'], ['files', 'Generated files'],
  ];
  for (const [seg, lbl] of jobMeta) {
    out.push(t(`mainapp.jobN.${seg}`, `Job card: ${lbl}`, { inactiveHint: ADD_A_JOB, filteredHint: SELECT_A_PHASE }));
  }
  out.push(t('mainapp.jobN.resumesections', 'Job card: Resume sections panel', { inactiveHint: ADD_A_JOB, filteredHint: SELECT_A_PHASE }));
  // Job edit panel fields (revealed by the edit pencil).
  for (const [seg, lbl] of [
    ['title', 'Title'], ['company', 'Company'], ['location', 'Location'], ['salary', 'Salary'],
    ['reqid', 'Req ID'], ['jobdesc', 'Job description'], ['jobtags', 'Job tags'],
  ] as const) {
    out.push(t(`mainapp.jobN.edit.${seg}`, `Job edit: ${lbl}`, { revealedBy: 'mainapp.jobN.edit', revealDelayMs: 320, inactiveHint: OPEN_JOB_EDIT }));
  }
  // Per-phase action buttons.
  for (const [phase, actions] of Object.entries(JOB_PHASE_ACTIONS)) {
    for (const a of actions) {
      out.push(t(`mainapp.jobN.${phase}.${a}`, `Job (${phase}): ${ACTION_LABELS[a] || a}`,
        { inactiveHint: ADD_A_JOB, filteredHint: SELECT_A_PHASE }));
    }
  }
  // Generated files: resume + cover letter and their context-menu actions.
  for (const [kind, lbl, extras] of [
    ['resume', 'Resume', ['regenerate', 'viewhtml', 'viewpdf', 'viewtext', 'workday']],
    ['coverletter', 'Cover Letter', ['regenerate', 'viewhtml', 'viewpdf', 'viewtext']],
  ] as [string, string, string[]][]) {
    out.push(t(`mainapp.jobN.files.${kind}`, `Files: ${lbl}`, { inactiveHint: ADD_A_JOB, filteredHint: SELECT_A_PHASE }));
    for (const ex of extras) {
      out.push(t(`mainapp.jobN.files.${kind}.${ex}`, `Files: ${lbl} → ${ex}`,
        { revealedBy: `mainapp.jobN.files.${kind}`, revealDelayMs: 200, inactiveHint: ADD_A_JOB, filteredHint: SELECT_A_PHASE }));
    }
  }

  // --- settings modal (opened from the user menu) ---
  const SETTINGS = 'mainapp.user.settings';
  const settingsSections: [string, string][] = [
    ['resumegen', 'Resume Generation'],
    ['jobretention', 'Job Retention'], ['security', 'Security'],
    ['subscription', 'Subscription'], ['communications', 'Communications'],
  ];
  for (const [seg, lbl] of settingsSections) {
    out.push(t(`${SETTINGS}.${seg}`, `Settings: ${lbl}`, { revealedBy: SETTINGS, revealDelayMs: 300 }));
  }
  // NOTE: Settings → Resume Sections is gone — section generation types, names
  // and order are per-resume now (Edit Resume lightbox targets above).
  // Resume Generation toggles.
  for (const [seg, lbl] of [
    ['watermark', 'SkillSnap watermark'], ['autodownload_on_open', 'Auto-download on open'],
    ['linkname_to_profile', 'Link name to profile'], ['skillsnap_resumebottom', 'SkillSnap credit at bottom'],
    ['showyear_education', 'Show year: Education'], ['showyear_awards', 'Show year: Awards'],
    ['showyear_presentations', 'Show year: Presentations'],
    ['showyear_publications', 'Show year: Publications'], ['showyear_portfolios', 'Show year: Portfolios'],
    ['bullet_endingperiod', 'Bullet ending period'], ['pagecount', 'Target page count'], ['yearcutoff', 'Year cutoff'],
    ['synonymdeclines', 'View declined synonym matches'],
  ] as const) {
    out.push(t(`${SETTINGS}.resumegen.${seg}`, `Settings → Resume Gen: ${lbl}`, { revealedBy: `${SETTINGS}.resumegen` }));
  }
  // Job Retention per phase.
  for (const [seg, lbl] of phaseLabels) {
    out.push(t(`${SETTINGS}.jobretention.${seg}`, `Settings → Retention: ${lbl}`, { revealedBy: `${SETTINGS}.jobretention` }));
  }
  // Security / Subscription / Communications items.
  out.push(
    t(`${SETTINGS}.security.changepassword`, 'Settings → Security: Change password', { revealedBy: `${SETTINGS}.security` }),
    t(`${SETTINGS}.security.emails`, 'Settings → Security: Forwarding Email Addresses', { revealedBy: `${SETTINGS}.security` }),
    t(`${SETTINGS}.subscription.status`, 'Settings → Subscription: Status', { revealedBy: `${SETTINGS}.subscription` }),
    t(`${SETTINGS}.subscription.plan`, 'Settings → Subscription: Plan', { revealedBy: `${SETTINGS}.subscription` }),
    t(`${SETTINGS}.subscription.upgrade`, 'Settings → Subscription: Upgrade', { revealedBy: `${SETTINGS}.subscription` }),
    t(`${SETTINGS}.subscription.organization`, 'Settings → Subscription: Organization', { revealedBy: `${SETTINGS}.subscription` }),
    t(`${SETTINGS}.communications.emailupdates`, 'Settings → Communications: Email updates', { revealedBy: `${SETTINGS}.communications` }),
  );

  // --- Application Q&A page ---
  out.push(
    t('answers.backtojobs', 'Answers: Back to Jobs'),
    t('answers.addqa', 'Answers: Add Q&A'),
    t('answers.search', 'Answers: Search'),
    t('answers.answer0', 'Answers: first answer card'),
  );

  // --- Implied Facts review lightbox (pops AFTER the editor closes) ---
  out.push(
    t('mainapp.impliedfactsreview.close', 'Implied Facts review: close', {
      inactiveHint: 'Appears after closing the Edit Resume lightbox, when new facts are found',
    }),
  );

  // --- Writing Style Fingerprint (Edit Resume section + lightbox) ---
  out.push(
    t('mainapp.resumes.resumeN.writingsample', 'Writing Sample section (resume editor)'),
    t('mainapp.resumes.resumeN.writingsample.open', 'Complete Writing Sample button', {
      inactiveHint: 'Open the Edit Resume lightbox first',
    }),
    t('mainapp.resumes.resumeN.writingsample.delete', 'Delete Writing Sample button', {
      inactiveHint: 'Visible once a fingerprint is on file (Edit Resume lightbox)',
    }),
    t('writingsample.modal.close', 'Writing Sample: close'),
    t('writingsample.modal.prompt', 'Writing Sample: starter prompts', {
      inactiveHint: 'Open the Writing Sample lightbox first',
    }),
    t('writingsample.modal.text', 'Writing Sample: writing box', {
      inactiveHint: 'Open the Writing Sample lightbox first',
    }),
    t('writingsample.modal.counter', 'Writing Sample: character counter', {
      inactiveHint: 'Open the Writing Sample lightbox first',
    }),
    t('writingsample.modal.analyze', 'Writing Sample: Generate My Fingerprint', {
      inactiveHint: 'Open the Writing Sample lightbox first',
    }),
    t('writingsample.modal.calibrate', 'Writing Sample: Start fine-tuning (calibration)', {
      inactiveHint: 'Generate a fingerprint first — the fine-tune option appears on the confirmation pane',
    }),
    t('writingsample.modal.calibrate.pick', 'Writing Sample: calibration pick buttons', {
      inactiveHint: 'Start the fine-tuning rounds first',
    }),
  );

  // --- Activity Dashboard page (route stays /activity-report) ---
  out.push(t('activity.backtojobs', 'Activity Dashboard: Back to Jobs'));

  // --- Interview Q&A page ---
  out.push(
    t('interview.backtojobs', 'Interview: Back to Jobs'),
    t('interview.search', 'Interview: quick search'),
    t('interview.addquestion', 'Interview: Add Question'),
    t('interview.question0', 'Interview: first question card'),
  );

  return out;
}

export const HELP_TARGETS: HelpTarget[] = buildCatalog();
// Back-compat alias (admin + older imports referenced HIGHLIGHT_TARGETS).
export const HIGHLIGHT_TARGETS = HELP_TARGETS;
export const HIGHLIGHT_TARGET_KEYS = HELP_TARGETS.map((x) => x.key);

const BY_KEY = new Map(HELP_TARGETS.map((x) => [x.key, x]));

export function highlightTargetLabel(key: string): string {
  return BY_KEY.get(key)?.label || key;
}

function elFor(key: string): HTMLElement | null {
  return document.querySelector<HTMLElement>(`[data-help-target="${CSS.escape(key)}"]`);
}

function isJobKey(key: string): boolean {
  return key.startsWith('mainapp.job');
}

/** Total jobs across all phases (filter-independent), published by the Sidebar. */
function totalJobs(): number {
  const el = document.querySelector('[data-help-jobs-total]');
  const n = el ? Number(el.getAttribute('data-help-jobs-total')) : NaN;
  return Number.isFinite(n) ? n : 0;
}

/** Non-mutating: is the target present, or revealable by opening its ancestors? */
export function isTargetActive(key: string): boolean {
  if (!key) return false;
  if (elFor(key)) return true;
  const def = BY_KEY.get(key);
  if (def?.revealedBy) return isTargetActive(def.revealedBy);
  return false;
}

/** Tooltip explaining why an inactive button can't act yet. Job targets pick the
 *  filtered hint when jobs exist but are hidden; otherwise inherit the blocking
 *  ancestor's hint (e.g. "Open a resume in the editor"). */
export function inactiveHint(key: string): string {
  const def = BY_KEY.get(key);
  if (isJobKey(key) && totalJobs() > 0 && !isTargetActive(key)) {
    return def?.filteredHint || SELECT_A_PHASE;
  }
  if (def?.revealedBy && !isTargetActive(def.revealedBy)) {
    const inherited = inactiveHint(def.revealedBy);
    if (inherited) return inherited;
  }
  return def?.inactiveHint || 'Not available on this screen';
}

// --- shared DOM watcher (inline buttons re-check on any DOM change) ---
const domListeners = new Set<() => void>();
let domObserver: MutationObserver | null = null;
let domTickScheduled = false;

function flushDomListeners() {
  domTickScheduled = false;
  domListeners.forEach((cb) => cb());
}

export function subscribeHelpDom(cb: () => void): () => void {
  if (!domObserver) {
    domObserver = new MutationObserver(() => {
      if (domTickScheduled) return;
      domTickScheduled = true;
      requestAnimationFrame(flushDomListeners);
    });
    domObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-help-target', 'data-help-jobs-total'],
    });
  }
  domListeners.add(cb);
  return () => {
    domListeners.delete(cb);
    if (domListeners.size === 0 && domObserver) {
      domObserver.disconnect();
      domObserver = null;
    }
  };
}

function delay(ms: number): Promise<void> {
  return new Promise((res) => window.setTimeout(res, ms));
}

/** Poll (via rAF) until the element for `key` appears or we time out. */
function waitForEl(key: string, timeoutMs: number): Promise<HTMLElement | null> {
  return new Promise((resolve) => {
    const start = performance.now();
    const tick = () => {
      const el = elFor(key);
      if (el) return resolve(el);
      if (performance.now() - start > timeoutMs) return resolve(null);
      requestAnimationFrame(tick);
    };
    tick();
  });
}

/** Recursively reveal a target: ensure its ancestor is shown, then click the
 *  ancestor to expose this target. Only clicks when the target is missing, so an
 *  already-open container is never toggled shut. */
async function reveal(key: string): Promise<boolean> {
  if (elFor(key)) return true;
  const def = BY_KEY.get(key);
  if (!def?.revealedBy) return false;
  if (!(await reveal(def.revealedBy))) return false;
  const opener = elFor(def.revealedBy);
  if (!opener) return false;
  opener.click();
  const found = await waitForEl(key, def.revealDelayMs ?? 260);
  return !!found;
}

function flash(key: string): boolean {
  const el = elFor(key); // first match = the one item we point at
  if (!el) return false;
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  el.classList.remove('help-highlight');
  void el.offsetWidth; // restart the animation
  el.classList.add('help-highlight');
  window.setTimeout(() => el.classList.remove('help-highlight'), 3200);
  return true;
}

/**
 * Reveal the target (opening any closed ancestors first) and flash it. An
 * optional `openFirst` list of target keys is clicked in order beforehand — the
 * manual override for paths the automatic reveal can't infer.
 */
export async function highlightTarget(key: string, openFirst?: string[]): Promise<boolean> {
  if (!key) return false;
  if (openFirst && openFirst.length) {
    for (const k of openFirst) {
      await reveal(k);
      elFor(k)?.click();
      await delay(160);
    }
  }
  if (!(await reveal(key))) return false;
  return flash(key);
}
